import React from "react";
import ModalForm from "@ui/Modal/Form";
import t from "../../../../i18n";
import Text from "@ui/Text";
import { useDelete, usePut } from "../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import { useNavigate } from "react-router-dom";
import { type SummaryDashboard } from "./utils";
import { useTranslations } from "../../../../api/useTranslations";
import { useMutate } from "../../../../api/useQuery";

interface P {
  projectId: string;
  dashboardId: string;
  dashboardName: string;
  nextTab: string;
  onRequestClose: () => void;
  summaryDashboards: SummaryDashboard[];
  setSummaryDashboards: React.Dispatch<React.SetStateAction<SummaryDashboard[]>>;
}

const ModalFormRemoveDashboard = ({
  projectId,
  dashboardId,
  dashboardName,
  nextTab,
  onRequestClose,
  summaryDashboards,
  setSummaryDashboards,
}: P) => {
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const translations = useTranslations();

  const deleteSummaryDashboard = useDelete(
    "/projects/:projectId(uuid)/summary/dashboards/:dashboardId(uuid)",
  );
  const updateSummaryDashboards = usePut("/projects/:projectId(uuid)/summary/dashboards");
  const mutateSumaryDashboard = useMutate("/projects/:projectId(uuid)/summary/dashboards", {
    routeParams: {
      projectId,
    },
  });

  const handleSubmit = React.useCallback(async () => {
    const removed = summaryDashboards.find(d => d.dashboardId === dashboardId);
    const previousOccupants = [...summaryDashboards];

    const reorderedDashboards = summaryDashboards
      .filter(d => d.dashboardId !== dashboardId)
      .map(d => {
        if (removed && d.position > removed.position) return { ...d, position: d.position - 1 };
        return d;
      });
    const rollback = mutateSumaryDashboard(previous => ({
      ...previous,
      body: [...previous.body].filter(d => d.dashboardId !== dashboardId),
    }));

    try {
      await deleteSummaryDashboard({ routeParams: { projectId, dashboardId } });
      await updateSummaryDashboards({ routeParams: { projectId }, body: reorderedDashboards });
      void navigate(nextTab);
      setSummaryDashboards(reorderedDashboards);
    } catch (e) {
      rollback();
      setSummaryDashboards(previousOccupants);
      addToast("error", t("errors.backend_error"));
    }

    return true;
  }, [
    summaryDashboards,
    setSummaryDashboards,
    dashboardId,
    deleteSummaryDashboard,
    projectId,
    updateSummaryDashboards,
    navigate,
    nextTab,
    addToast,
    mutateSumaryDashboard,
  ]);

  return (
    <ModalForm
      onSubmit={handleSubmit}
      onRequestClose={onRequestClose}
      okButtonName={t("modalForm.button.name.remove")}
      okButtonVariant="destructive"
    >
      <Text>
        {t(
          "summary.dashboard.modal.remove.text",
          {
            dashboardName: (
              <Text type="body1SemiBold" as="span">
                {dashboardName}
              </Text>
            ),
          },
          translations,
        )}
      </Text>
    </ModalForm>
  );
};

export default React.memo(ModalFormRemoveDashboard);
